/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\components\WidgetWrapper.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import { useState, useMemo, ReactNode, createContext } from 'react';
import { GripVertical, X, Settings, Eye, EyeOff, Pencil, Check } from 'lucide-react';
import { useThemeStore, WidgetPrefs } from '../../../store/theme';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';

export const WidgetContext = createContext<WidgetPrefs | undefined>(undefined);

// Default widget preferences (fallback)
const DEFAULT_WIDGET_DARK = {
    borderRadius: 12,
    opacity: 100,
    headerVisible: true,
    shadowIntensity: 'medium' as const,
    headerColor: '#1e293b',
    bodyColor: '#0f172a',
    textColor: '#f1f5f9',
    borderColor: '#334155',
    cardColor: '#1e293b'
};

const DEFAULT_WIDGET_LIGHT = {
    borderRadius: 12,
    opacity: 100,
    headerVisible: true,
    shadowIntensity: 'medium' as const,
    headerColor: '#ffffff',
    bodyColor: '#ffffff',
    textColor: '#1e293b',
    borderColor: '#e2e8f0',
    cardColor: '#ffffff'
};

interface WidgetWrapperProps {
    title: string;
    widgetType: string;
    isEditMode: boolean;
    isVisible?: boolean;
    children: ReactNode;
    onDelete?: () => void;
    onTitleChange?: (newTitle: string) => void;
    onToggleVisibility?: () => void;
    onConfigure?: () => void;
}

export function WidgetWrapper({
    title,
    widgetType,
    isEditMode,
    isVisible = true,
    children,
    onDelete,
    onTitleChange,
    onToggleVisibility,
    onConfigure,
}: WidgetWrapperProps) {
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState(title);

    // Get widget appearance settings from theme store
    const { themePrefs, themeMode } = useThemeStore();

    // Determine actual mode (handle 'system')
    const actualMode = useMemo(() => {
        if (themeMode === 'system') {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        }
        return themeMode;
    }, [themeMode]);

    const defaults = actualMode === 'dark' ? DEFAULT_WIDGET_DARK : DEFAULT_WIDGET_LIGHT;

    // Determine active widget preferences based on current mode
    const activeWidgetPrefs = useMemo(() => {
        if (actualMode === 'dark') {
            return themePrefs.widgetDark || themePrefs.widget || DEFAULT_WIDGET_DARK;
        } else {
            return themePrefs.widgetLight || DEFAULT_WIDGET_LIGHT;
        }
    }, [actualMode, themePrefs.widget, themePrefs.widgetDark, themePrefs.widgetLight]);

    // Use the active prefs
    const widgetPrefs = activeWidgetPrefs;

    // Compute dynamic styles based on widget preferences
    const widgetStyles = useMemo(() => {
        const borderRadius = widgetPrefs?.borderRadius ?? defaults.borderRadius;
        const opacity = widgetPrefs?.opacity ?? defaults.opacity;
        const shadowIntensity = widgetPrefs?.shadowIntensity || defaults.shadowIntensity;
        // Prioritize bodyColor to match Appearance Preview (User expectation)
        const backgroundColor = widgetPrefs?.bodyColor || widgetPrefs?.cardColor || defaults.bodyColor;
        const borderColor = widgetPrefs?.borderColor || defaults.borderColor;

        const shadowMap: Record<string, string> = {
            none: 'none',
            light: '0 1px 3px rgba(0,0,0,0.1)',
            medium: '0 4px 6px rgba(0,0,0,0.15)',
            heavy: '0 10px 25px rgba(0,0,0,0.25)'
        };

        return {
            borderRadius: `${borderRadius}px`,
            opacity: opacity / 100,
            boxShadow: shadowMap[shadowIntensity] || shadowMap.medium,
            backgroundColor: backgroundColor,
            borderColor: borderColor,
            borderWidth: '1px',
            borderStyle: 'solid' as const
        };
    }, [widgetPrefs, defaults]);

    // Compute header styles
    const headerStyles = useMemo(() => {
        const headerColor = widgetPrefs?.headerColor || defaults.headerColor;
        const borderColor = widgetPrefs?.borderColor || defaults.borderColor;
        return {
            backgroundColor: headerColor,
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid' as const,
            borderBottomColor: borderColor
        };
    }, [widgetPrefs, defaults]);

    // Text color from preferences
    const textColor = widgetPrefs?.textColor || defaults.textColor;

    // Determine if header should be visible
    const headerVisible = widgetPrefs?.headerVisible ?? true;

    const handleTitleSave = () => {
        if (editedTitle.trim() && onTitleChange) {
            onTitleChange(editedTitle.trim());
        }
        setIsEditingTitle(false);
    };

    const handleTitleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleTitleSave();
        } else if (e.key === 'Escape') {
            setEditedTitle(title);
            setIsEditingTitle(false);
        }
    };

    return (
        <WidgetContext.Provider value={widgetPrefs}>
            <div
                className={`h-full flex flex-col overflow-hidden transition-all ${!isVisible ? 'opacity-50' : ''}`}
                style={widgetStyles}
            >
                {/* Widget Header - respects headerVisible setting */}
                {headerVisible && (
                    <div
                        className={`widget-header flex items-center justify-between px-3 py-2 ${isEditMode ? 'cursor-move' : ''}`}
                        style={headerStyles}
                    >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                            {isEditMode && (
                                <GripVertical className="w-4 h-4 flex-shrink-0" style={{ color: textColor, opacity: 0.6 }} />
                            )}

                            {isEditingTitle ? (
                                <div className="flex items-center gap-1 flex-1">
                                    <input
                                        type="text"
                                        value={editedTitle}
                                        onChange={(e) => setEditedTitle(e.target.value)}
                                        onBlur={handleTitleSave}
                                        onKeyDown={handleTitleKeyDown}
                                        className="flex-1 text-sm font-medium bg-background border border-border rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-primary"
                                        autoFocus
                                    />
                                    <button
                                        onClick={handleTitleSave}
                                        className="p-1 hover:bg-primary/10 rounded text-primary"
                                    >
                                        <Check className="w-3 h-3" />
                                    </button>
                                </div>
                            ) : (
                                <span
                                    className="text-sm font-medium truncate cursor-default"
                                    style={{ color: textColor }}
                                    onDoubleClick={() => isEditMode && setIsEditingTitle(true)}
                                >
                                    {title}
                                </span>
                            )}
                        </div>

                        {/* Edit Mode Controls */}
                        {isEditMode && (
                            <div className="flex items-center gap-1">
                                {!isEditingTitle && (
                                    <SimpleTooltip content="Edit title">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsEditingTitle(true);
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            className="p-1 hover:bg-white/10 rounded transition-colors"
                                            style={{ color: textColor, opacity: 0.6 }}
                                        >
                                            <Pencil className="w-3 h-3" />
                                        </button>
                                    </SimpleTooltip>
                                )}

                                {onToggleVisibility && (
                                    <SimpleTooltip content={isVisible ? "Hide widget" : "Show widget"}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onToggleVisibility();
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            className="p-1 hover:bg-white/10 rounded transition-colors"
                                            style={{ color: textColor, opacity: 0.6 }}
                                        >
                                            {isVisible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                        </button>
                                    </SimpleTooltip>
                                )}

                                {onConfigure && (
                                    <SimpleTooltip content="Configure">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onConfigure();
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            className="p-1 hover:bg-white/10 rounded transition-colors"
                                            style={{ color: textColor, opacity: 0.6 }}
                                        >
                                            <Settings className="w-3 h-3" />
                                        </button>
                                    </SimpleTooltip>
                                )}

                                {onDelete && (
                                    <SimpleTooltip content="Remove widget">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                onDelete();
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onTouchStart={(e) => e.stopPropagation()}
                                            className="p-1 hover:bg-red-500/20 rounded transition-colors"
                                            style={{ color: textColor, opacity: 0.6 }}
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </SimpleTooltip>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Widget Content */}
                <div className="flex-1 p-3 overflow-auto">
                    {children}
                </div>
            </div>
        </WidgetContext.Provider>
    );
}
